<template>
    <div>
        <div
            role="meter"
            :aria-label="t('profile.perspective.dimensionBar.meterA11yLabel', { left: dimensionLabel('left'), right: dimensionLabel('right') })"
            :aria-valuenow="binnedScore"
            :aria-valuemin="-3"
            :aria-valuemax="3"
            :aria-valuetext="dimensionValueText(binnedScore)"
            class="relative h-2.5 bg-surface-sunken rounded-full">
            <div
                class="absolute top-0 bottom-0 rounded-full transition-[left,background-color] duration-1000 motion-reduce:transition-[background-color]"
                :class="dimensionColor(code)"
                :style="pillStyle(binnedScore)" />
        </div>
        <div class="flex justify-between mt-2">
            <span class="flex items-center gap-2" :class="binnedScore < 0 ? 'body-small-bold text-ink' : 'body-small text-ink-muted'">
                <PerspectiveDot v-if="binnedScore < 0" :dimension="code" size="sm" />
                {{ dimensionLabel("left") }}
            </span>
            <span class="flex items-center gap-2" :class="binnedScore >= 0 ? 'body-small-bold text-ink' : 'body-small text-ink-muted'">
                {{ dimensionLabel("right") }}
                <PerspectiveDot v-if="binnedScore >= 0" :dimension="code" size="sm" />
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { type Dimension, DIMENSION_COLORS } from "~vue/pages/perspectiveTypes";
import PerspectiveDot from "~vue/Perspective/PerspectiveDot.vue";
import { scoreToPercent } from "~vue/utils/perspectiveUtils";
import { useI18n } from "vue-i18n";

type DimensionBarProps = {
    code: Dimension;
    binnedScore: number;
    leftLabel: string;
    rightLabel: string;
};

const { leftLabel, rightLabel } = defineProps<DimensionBarProps>();

const { t } = useI18n();

function dimensionLabel(side: "left" | "right"): string {
    return side === "left" ? leftLabel : rightLabel;
}

function dimensionColor(code: Dimension): string {
    return DIMENSION_COLORS[code];
}

function dimensionValueText(binnedScore: number): string {
    if (binnedScore === 0) {
        return t("profile.perspective.dimensionBar.valueBalanced");
    }
    const dominant = binnedScore > 0 ? dimensionLabel("right") : dimensionLabel("left");
    const strength = Math.abs(binnedScore) <= 1 ? "slight" : Math.abs(binnedScore) <= 2 ? "moderate" : "strong";
    return t("profile.perspective.dimensionBar.valuePreference", { strength, dominant });
}

function pillStyle(binnedScore: number): { left: string; width: string } {
    const percent = scoreToPercent(binnedScore);
    const left = Math.max(0, Math.min(percent - 14, 72));
    return { left: `${left}%`, width: "27%" };
}
</script>
